import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDinoDuos } from '@/hooks/useDinoDuos';
import { BuddyReadCard } from '@/components/BuddyReadCard';
import { CreateDinoDuoDialog } from '@/components/CreateDinoDuoDialog';
import { EmptyState } from '@/components/EmptyState';
import { Users, Plus, BookOpen, Check, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

export default function DinoDuos() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { dinoDuos, loading, createDinoDuo, acceptInvite } = useDinoDuos();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Fetch pending invitations
  const { data: invitations, refetch: refetchInvitations } = useQuery({
    queryKey: ['buddy-read-invitations', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data: memberData, error: memberError } = await supabase
        .from('buddy_read_members')
        .select(`
          *,
          buddy_read:buddy_reads(
            *,
            book:books(id, title, authors, cover_url, page_count),
            creator:profiles!buddy_reads_created_by_fkey(id, username, name, image)
          )
        `)
        .eq('user_id', user!.id)
        .eq('status', 'invited');

      if (memberError) throw memberError;

      return memberData?.map(m => ({
        ...m,
        buddy_read: Array.isArray(m.buddy_read) ? m.buddy_read[0] : m.buddy_read
      })) || [];
    }
  });

  // Set up real-time subscription for new invitations
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('buddy-read-invitations')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new.status === 'invited') {
            refetchInvitations();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          // Refetch if status changed (accepted/declined)
          if (payload.old.status !== payload.new.status) {
            refetchInvitations();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refetchInvitations]);

  const handleAcceptInvite = async (buddyReadId: string) => {
    await acceptInvite(buddyReadId);
    refetchInvitations();
  };

  const handleDeclineInvite = async (buddyReadId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({ status: 'declined' })
        .eq('buddy_read_id', buddyReadId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      toast({
        title: "Invitation declined",
        description: "You've declined the buddy read invitation."
      });
      
      refetchInvitations();
    } catch (error) {
      console.error('Error declining invite:', error);
      toast({
        title: "Error",
        description: "Failed to decline invitation. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold gradient-text mb-2 flex items-center gap-3">
              🦕 Dino Duos
            </h1>
            <p className="text-muted-foreground">
              Read together with friends, share progress, and walk the reading trail side by side
            </p>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Create a Duo
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList>
            <TabsTrigger value="active" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Active Duos
              {dinoDuos.length > 0 && (
                <Badge variant="secondary">{dinoDuos.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="invitations" className="gap-2">
              <Users className="h-4 w-4" />
              Invitations
              {invitations && invitations.length > 0 && (
                <Badge variant="destructive">{invitations.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Active Dino Duos */}
          <TabsContent value="active" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
                <p className="text-muted-foreground mt-4">Loading your duos...</p>
              </div>
            ) : dinoDuos.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No Active Dino Duos"
                description="🦕 Start reading with friends! Create your first Duo to walk the reading trail together."
                action={
                  <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create a Duo
                  </Button>
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dinoDuos.map((duo) => (
                  <BuddyReadCard key={duo.id} buddyRead={duo} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Invitations */}
          <TabsContent value="invitations" className="space-y-4">
            {!invitations || invitations.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No Pending Invitations"
                description="You don't have any Dino Duo invitations at the moment."
              />
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation: any) => (
                  <Card key={invitation.id} className="card-elevated">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        {/* Book Cover */}
                        <img
                          src={invitation.buddy_read?.book?.cover_url || '/placeholder.svg'}
                          alt={invitation.buddy_read?.book?.title}
                          className="w-24 h-32 object-cover rounded shadow-sm"
                        />

                        {/* Content */}
                        <div className="flex-1 space-y-3">
                          {/* Invitation Info */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar className="h-6 w-6">
                                <AvatarImage src={invitation.buddy_read?.creator?.image} />
                                <AvatarFallback>
                                  {invitation.buddy_read?.creator?.username?.[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-muted-foreground">
                                 <span className="font-medium text-foreground">
                                  {invitation.buddy_read?.creator?.name || invitation.buddy_read?.creator?.username}
                                </span>
                                {' '}invited you to a Dino Duo 🦕
                              </span>
                            </div>
                            
                            {invitation.buddy_read?.title && (
                              <h3 className="font-semibold text-lg">{invitation.buddy_read.title}</h3>
                            )}
                            <p className="font-medium">
                              {invitation.buddy_read?.book?.title}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              by {invitation.buddy_read?.book?.authors?.join(', ')}
                            </p>
                          </div>

                          {/* Dates */}
                          {(invitation.buddy_read?.start_date || invitation.buddy_read?.target_end_date) && (
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              {invitation.buddy_read?.start_date && (
                                <span>
                                  Starts: {format(new Date(invitation.buddy_read.start_date), 'MMM d, yyyy')}
                                </span>
                              )}
                              {invitation.buddy_read?.target_end_date && (
                                <span>
                                  Target: {format(new Date(invitation.buddy_read.target_end_date), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleAcceptInvite(invitation.buddy_read.id)}
                              className="gap-2"
                            >
                              <Check className="h-4 w-4" />
                              Accept
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleDeclineInvite(invitation.buddy_read.id)}
                              className="gap-2"
                            >
                              <X className="h-4 w-4" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Dialog */}
      <CreateDinoDuoDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreateDuo={createDinoDuo}
      />
    </div>
  );
}
